import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { CsvAsStringArrays, getPlatformDefinition, isNotNil } from '@malou-io/package-utils';

import { ReviewsRatingsAverageData } from '#modules/statistics-e-reputation/components/aggregated-e-reputation/reviews/models/reviews.interface';
import { AbstractCsvService } from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';
import { CsvNumberFormatter } from '#shared/helpers/number-formatter';

type Data = ReviewsRatingsAverageData[];

@Injectable()
export class AggregatedAverageReviewsRatingsCsvInsightsService extends AbstractCsvService<Data> {
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);
    private readonly _csvNumberFormatter = inject(CsvNumberFormatter);

    protected override _isDataValid(data: Data): boolean {
        return data.length !== 0;
    }

    protected override _getData$(): Observable<Data> {
        return of(this._aggregatedStatisticsStore.averageReviewsRatingsData());
    }

    protected override _getCsvDataRows(data: Data): CsvAsStringArrays {
        const platformsWithData = this._getPlatformsWithData(data);
        return data.map((d) => {
            const restaurantName = d.restaurant.name;
            const restaurantInternalName = d.restaurant.internalName ?? '';
            const restaurantAddress = d.restaurant.address?.getFullFormattedAddress() ?? '';
            const averageReviewsRatings = typeof d.averageRating === 'number' ? this._csvNumberFormatter.format(d.averageRating) : '';
            const platformsAverageReviewsRatings = this._getPlatformAverageReviewsRatings(d, platformsWithData);
            return [restaurantName, restaurantInternalName, restaurantAddress, averageReviewsRatings, ...platformsAverageReviewsRatings];
        });
    }

    protected override _getCsvHeaderRow(data: Data): CsvAsStringArrays[0] {
        const platformsWithData = this._getPlatformsWithData(data);
        const platformsFullNames = platformsWithData.map((p) => getPlatformDefinition(p)?.fullName).filter(isNotNil);
        const platformsHeaders = platformsFullNames.map((p) => `Reviews Average Rating (${p})`);
        return ['Location', 'Location Internal Name', 'Location Address', 'Reviews Average Rating (All platforms)', ...platformsHeaders];
    }

    private _getPlatformsWithData(data: Data): string[] {
        const platformsWithData = data.map((d) => d.byPlatforms.map((p) => p.platformKey)).flat();
        // Sorted so the column order does not depend on the order of the reviews in the API response.
        return [...new Set(platformsWithData)].sort();
    }

    private _getPlatformAverageReviewsRatings(data: ReviewsRatingsAverageData, platformKeys: string[]): string[] {
        return platformKeys.map((platformKey) => {
            const platformData = data.byPlatforms.find((p) => p.platformKey === platformKey);
            if (!platformData) {
                return '-';
            }
            return typeof platformData.average === 'number' ? this._csvNumberFormatter.format(platformData.average) : '-';
        });
    }
}
